import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Check = {
  name: string;
  label: string;
  status: "pass" | "fail" | "warn";
  ms: number;
  message?: string;
  required: boolean;
};

type Health = {
  status: "healthy" | "degraded" | "unhealthy";
  ready: boolean;
  checkedAt: string;
  durationMs: number;
  summary: { total: number; passed: number; failed: number; warnings: number };
  checks: Check[];
};

export const Route = createFileRoute("/health")({
  head: () => ({
    meta: [
      { title: "Xamseen — Launch Readiness" },
      {
        name: "description",
        content:
          "Live readiness checklist for Xamseen: authentication, database reads and writes, media storage and map tile delivery.",
      },
      { property: "og:title", content: "Launch Readiness — Xamseen" },
      {
        property: "og:description",
        content: "Live status of Xamseen's authentication, database, storage and map services.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HealthPage,
});

function HealthPage() {
  const { data, isFetching, refetch, error } = useQuery<Health>({
    queryKey: ["health"],
    queryFn: async () => {
      const res = await fetch("/api/public/health", { cache: "no-store" });
      return (await res.json()) as Health;
    },
    refetchInterval: 60_000,
  });

  const tone =
    data?.status === "healthy"
      ? "text-emerald-600"
      : data?.status === "degraded"
        ? "text-amber-600"
        : "text-destructive";

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-10">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Launch readiness</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Automated checks against authentication, the database, media storage and map tiles.
        </p>
      </header>

      <div className="mb-6 flex items-center justify-between rounded-2xl border bg-card p-5">
        <div>
          <p className={cn("text-lg font-semibold capitalize", tone)}>
            {error ? "unreachable" : (data?.status ?? "checking…")}
          </p>
          <p className="text-xs text-muted-foreground">
            {data
              ? `${data.summary.passed}/${data.summary.total} checks passing · ${data.durationMs} ms · ${new Date(data.checkedAt).toLocaleTimeString()}`
              : "Running probes"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Re-run
        </Button>
      </div>

      <ul className="divide-y overflow-hidden rounded-2xl border bg-card">
        {(data?.checks ?? []).map((c) => (
          <li key={c.name} className="flex items-start gap-3 p-4">
            {c.status === "pass" ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            ) : c.status === "warn" ? (
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{c.label}</p>
              {c.message && <p className="truncate text-xs text-muted-foreground">{c.message}</p>}
            </div>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{c.ms} ms</span>
          </li>
        ))}
        {!data && !error && (
          <li className="p-4 text-sm text-muted-foreground">Running checks…</li>
        )}
        {error && <li className="p-4 text-sm text-destructive">Could not reach the health endpoint.</li>}
      </ul>

      <p className="mt-4 text-xs text-muted-foreground">
        Machine-readable results: <code>/api/public/health</code> — returns 200 when ready, 503 when a required
        check fails.
      </p>
    </main>
  );
}
